"use client";

import { ClockIcon, MapPinIcon } from "@heroicons/react/24/outline";
import React, { useState, useRef, useEffect, FC } from "react";
import ClearDataButton from "./ClearDataButton";
import useFetch from "hooks/useFetch";
import config from "config.json"
import { Session } from "inspector";

interface Hotel {
  codigo: string;
  descripcion: string;
  ciudad: string;
  direccion: string;
  precio_por_noche: string;
  cant_lugares: number;
  banios_compartidos: number;
  banios_habitaciones: number;
}

export interface LocationInputProps {
  placeHolder?: string;
  desc?: string;
  className?: string;
  divHideVerticalLineClass?: string;
  autoFocus?: boolean;
}

const LocationInput: FC<LocationInputProps> = ({
  autoFocus = false,
  placeHolder = "Busca tu Hotel",
  desc = "Ingresa tu busqueda",
  className = "nc-flex-1.5",
  divHideVerticalLineClass = "left-10 -right-0.5",
}) => {

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [value, setValue] = useState("");
  const [showPopover, setShowPopover] = useState(autoFocus);
  const [hotels, setHotels] = useState<Hotel[]>([]); 
  const [filteredHotels, setFilteredHotels] = useState<Hotel[]>([]); 

  const url = config.backend_url;
  
  
  const { data, error, isLoading, fetchData } = useFetch<Hotel[]>(
    'GET',
    `${url}/setia/hoteles`,
    {}
  );

  useEffect(() => {
    sessionStorage.removeItem("selected_hotel");
    fetchData(); 
  }, []);

  useEffect(() => {
    if (data) {
      const response = data as any
      setHotels(response.hoteles);
      setFilteredHotels(response.hoteles);
      sessionStorage.removeItem("selected_hotel");
      /* sessionStorage.setItem("all_hotels", JSON.stringify(response.hoteles));  */
    }
  }, [data]);
  
  useEffect(() => {
    if (eventClickOutsideDiv) {
      document.removeEventListener("click", eventClickOutsideDiv);
    }
    showPopover && document.addEventListener("click", eventClickOutsideDiv);
    return () => {
      document.removeEventListener("click", eventClickOutsideDiv);
    };

  }, [showPopover]);

  const handleSelectLocation = (item: string) => {
    setValue(item);
    setShowPopover(false);
    
    // Busca el hotel seleccionado en la lista filtrada
    const selectedHotel = filteredHotels.find(hotel => hotel.descripcion.toLowerCase() === item.toLowerCase());
  
    // Si se encuentra un hotel seleccionado, guarda su código en el sessionStorage
    if (selectedHotel) {
      const code = selectedHotel.codigo;
      sessionStorage.setItem("selected_hotel", code);
      console.log('Código del hotel seleccionado:', code);
    } else {
      // Si no se selecciona ningún hotel, elimina el código del sessionStorage
      sessionStorage.removeItem("selected_hotel");
      console.log('Ningún hotel seleccionado');
    }
  };

  const handleClearData = () => {
    setValue("");
    setFilteredHotels(hotels);
    setShowPopover(false);
    sessionStorage.removeItem("selected_hotel");
    console.log('Datos limpiados');
  };

  const handleSelectLocationChange = (item: string) => {
    setValue(item);
    setShowPopover(false);
    sessionStorage.setItem("selected_hotel", item);
  };

  const eventClickOutsideDiv = (event: MouseEvent) => {
    if (!containerRef.current) return;
 
    if (!showPopover || containerRef.current.contains(event.target as Node)) {
      return;
    }
    // CLICK OUT_SIDE
    setShowPopover(false);
  };

  // Función para manejar el cambio en el input de búsqueda
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setValue(inputValue);

    const filteredResults = hotels.filter(hotel =>
      hotel.descripcion.toLowerCase().includes(inputValue.toLowerCase()) ||
      hotel.ciudad.toLowerCase().includes(inputValue.toLowerCase())
    );
    setFilteredHotels(filteredResults);
  };

  const renderSearchValue = () => {
    if (filteredHotels.length === 0) {
      return (
        <span className="block px-4 sm:px-8 py-2 text-sm text-neutral-700 dark:text-neutral-200">
          No se encontraron resultados.
        </span>
      );
    }
    return (
      <>
        {filteredHotels.map((hotel) => (
          <span
            onClick={() => handleSelectLocation(hotel.descripcion)}
            key={hotel.codigo}
            className="flex px-4 sm:px-8 items-center space-x-3 sm:space-x-4 py-4 hover:bg-neutral-100 dark:hover:bg-neutral-700 cursor-pointer"
          >
            <span className="block text-neutral-400">
              <MapPinIcon className="h-4 w-4 sm:h-6 sm:w-6" />
            </span>
            <span className="block font-medium text-neutral-700 dark:text-neutral-200">
              {hotel.descripcion}
            </span>
            
            <span className="block text-neutral-500 dark:text-neutral-400 text-sm">
             {hotel.ciudad}
            </span>
          </span>
        ))}
      </>
    );
  };

  return (
    <div className={`relative flex ${className}`} ref={containerRef}>
      <div
        onClick={() => setShowPopover(true)}
        className={`flex z-10 flex-1 relative [ nc-hero-field-padding ] flex-shrink-0 items-center space-x-3 cursor-pointer focus:outline-none text-left  ${showPopover ? "nc-hero-field-focused" : ""
          }`}
      >
        <div className="text-neutral-300 dark:text-neutral-400">
          <MapPinIcon className="w-5 h-5 lg:w-7 lg:h-7" />
        </div>
        <div className="flex-grow">
          <input
            className={`block w-full bg-transparent border-none focus:ring-0 p-0 focus:outline-none focus:placeholder-neutral-300 xl:text-lg font-semibold placeholder-neutral-800 dark:placeholder-neutral-200 truncate`}
            placeholder={placeHolder}
            value={value}
            autoFocus={showPopover}
            onChange={handleInputChange}
            ref={inputRef}
          />
          <span className="block mt-0.5 text-sm text-neutral-400 font-light ">
            <span className="line-clamp-1">{!!value ? placeHolder : desc}</span>
          </span>
          {value && showPopover && (
            <ClearDataButton
              onClick={handleClearData}
            />
          )}
        </div>
      </div>

      {showPopover && (
        <div
          className={`h-8 absolute self-center top-1/2 -translate-y-1/2 z-0 bg-white dark:bg-neutral-800 ${divHideVerticalLineClass}`}
        ></div>
      )}

      {showPopover && (
        <div className="absolute left-0 z-40 w-full min-w-[300px] sm:min-w-[500px] bg-white dark:bg-neutral-800 top-full mt-3 py-3 sm:py-6 rounded-3xl shadow-xl max-h-96 overflow-y-auto">
          {renderSearchValue()}
        </div>
      )}
    </div>
  );
};

export default LocationInput;